import type {
  BookId,
  ChecklistBookPage,
  ChecklistDefinition,
  ChecklistDetailVersion,
  ChecklistLayoutVersion,
  ChecklistType,
  DetailVersionId,
  LayoutVersionId,
  PageKey,
} from '../types/checklistBooks'

const pageCache = new Map<string, Promise<ChecklistBookPage | null>>()
const definitionListCache = new Map<string, Promise<ChecklistDefinition[]>>()
const layoutVersionCache = new Map<string, Promise<ChecklistLayoutVersion | null>>()
const detailVersionCache = new Map<string, Promise<ChecklistDetailVersion | null>>()

const remember = <T>(
  cache: Map<string, Promise<T>>,
  key: string,
  loader: () => Promise<T>,
): Promise<T> => {
  const existing = cache.get(key)
  if (existing) return existing
  const pending = loader().catch((error) => {
    cache.delete(key)
    throw error
  })
  cache.set(key, pending)
  return pending
}

const prime = <T>(
  cache: Map<string, Promise<T>>,
  key: string,
  value: T,
) => {
  cache.set(key, Promise.resolve(value))
}

const pageCacheKey = (bookId: BookId, pageKey: PageKey) => `${bookId}::${pageKey}`
const definitionListCacheKey = (workspaceId: string, type: ChecklistType) => `${workspaceId}::${type}`
const layoutVersionCacheKey = (bookId: BookId, versionId: LayoutVersionId) => `${bookId}::${versionId}`
const detailVersionCacheKey = (bookId: BookId, versionId: DetailVersionId) => `${bookId}::${versionId}`

export const readCachedChecklistPage = (
  bookId: BookId,
  pageKey: PageKey,
  loader: () => Promise<ChecklistBookPage | null>,
): Promise<ChecklistBookPage | null> =>
  remember(pageCache, pageCacheKey(bookId, pageKey), loader)

export const primeChecklistPageCache = (
  bookId: BookId,
  pageKey: PageKey,
  value: ChecklistBookPage | null,
) => {
  prime(pageCache, pageCacheKey(bookId, pageKey), value)
}

export const invalidateChecklistPageCache = (
  bookId: BookId,
  pageKey: PageKey,
) => {
  pageCache.delete(pageCacheKey(bookId, pageKey))
}

export const invalidateChecklistBookPageCache = (bookId: BookId) => {
  const prefix = `${bookId}::`
  Array.from(pageCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) pageCache.delete(key)
  })
}

export const readCachedChecklistDefinitions = (
  workspaceId: string,
  type: ChecklistType,
  loader: () => Promise<ChecklistDefinition[]>,
): Promise<ChecklistDefinition[]> =>
  remember(definitionListCache, definitionListCacheKey(workspaceId, type), loader)

export const primeChecklistDefinitionListCache = (
  workspaceId: string,
  type: ChecklistType,
  value: ChecklistDefinition[],
) => {
  prime(definitionListCache, definitionListCacheKey(workspaceId, type), value)
}

export const invalidateChecklistDefinitionListCache = (
  workspaceId: string,
  type: ChecklistType,
) => {
  definitionListCache.delete(definitionListCacheKey(workspaceId, type))
}

export const readCachedLayoutVersion = (
  bookId: BookId,
  versionId: LayoutVersionId,
  loader: () => Promise<ChecklistLayoutVersion | null>,
): Promise<ChecklistLayoutVersion | null> =>
  remember(layoutVersionCache, layoutVersionCacheKey(bookId, versionId), loader)

export const primeChecklistLayoutVersionCache = (
  bookId: BookId,
  versionId: LayoutVersionId,
  value: ChecklistLayoutVersion | null,
) => {
  prime(layoutVersionCache, layoutVersionCacheKey(bookId, versionId), value)
}

export const readCachedDetailVersion = (
  bookId: BookId,
  versionId: DetailVersionId,
  loader: () => Promise<ChecklistDetailVersion | null>,
): Promise<ChecklistDetailVersion | null> =>
  remember(detailVersionCache, detailVersionCacheKey(bookId, versionId), loader)

export const primeChecklistDetailVersionCache = (
  bookId: BookId,
  versionId: DetailVersionId,
  value: ChecklistDetailVersion | null,
) => {
  prime(detailVersionCache, detailVersionCacheKey(bookId, versionId), value)
}
